/**
 * Drift-topology store — the seam that carries live-conformance drift UP to the
 * board solver across the provider hierarchy.
 *
 * The knot: the solver (`useBoardLayout`, in `LayoutProvider`) sits ABOVE the
 * live-telemetry providers, but the drift topology (observed-but-unmodeled call
 * pairs) is derived DOWN in the telemetry layer. React context flows one way, so
 * neither side can read the other. A module-level external store sidesteps the
 * hierarchy entirely: the telemetry layer publishes here, and `LayoutProvider`
 * subscribes — the same trick `hoverStore` uses, but board-GLOBAL (drift is a
 * property of the whole model, not one space), so a singleton fits better than
 * a per-board context store.
 *
 * The critical property: {@link setDriftTopology} dedupes on the topology KEY, so
 * a metric-only poll (same drift shape, new rates) is a no-op — the array
 * identity is stable, `useBoardLayout`'s memo doesn't fire, and the board does
 * NOT re-solve. Only a change in the drift SHAPE re-lays-out.
 */
import { useSyncExternalStore } from "react";
import type { UndocumentedPair } from "../hooks/useBoardLayout.js";

let current: readonly UndocumentedPair[] = [];
let currentKey = "";
const listeners = new Set<() => void>();

function keyOf(pairs: readonly UndocumentedPair[]): string {
  return pairs.map((p) => `${p.from} ${p.to}`).join("|");
}

/** Publish the current drift topology. A no-op when the SHAPE is unchanged
 *  (metric-only poll) — keeps the array identity stable so the board doesn't
 *  re-solve. Callers pass an already-sorted, deduped set (see `driftTopology`). */
export function setDriftTopology(pairs: readonly UndocumentedPair[]): void {
  const key = keyOf(pairs);
  if (key === currentKey) return;
  current = pairs;
  currentKey = key;
  for (const l of listeners) l();
}

export function getDriftTopology(): readonly UndocumentedPair[] {
  return current;
}

/** Subscribe to shape changes (fires only when the drift SHAPE changes). Returns
 *  an unsubscribe. The hook builds on this; exposed for non-React consumers. */
export function subscribeDriftTopology(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** Test-only: clear the published topology. */
export function _resetDriftTopology(): void {
  current = [];
  currentKey = "";
  for (const l of listeners) l();
}

/** Subscribe the board solver to the live drift topology. Re-renders (and thus
 *  re-solves) only when the drift SHAPE changes. */
export function useDriftTopology(): readonly UndocumentedPair[] {
  return useSyncExternalStore(subscribeDriftTopology, getDriftTopology, getDriftTopology);
}
